import ArrowBackTwoToneIcon from "@mui/icons-material/ArrowBackTwoTone";
import ArrowForwardTwoToneIcon from "@mui/icons-material/ArrowForwardTwoTone";
import { useNavigate, useLocation, useParams, NavLink, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Title } from "../../types/Title";
import useTitlesService from "../../services/TitlesService";
import PageTitle from "../../components/PageTitle";
import LoadingEllipsis from "../../components/LoadingEllipsis";
import ExperienceDetailForm from "../../components/Experiences/ExperienceDetailForm";
import Ghost from "../../components/Ghost";
import ExperienceKeyCard from "../../components/Experiences/ExperienceKeyCard";
import AddTwoToneIcon from "@mui/icons-material/AddTwoTone";
import ExperienceBlockCard from "../../components/Experiences/ExperienceBlockCard";
import BackButton from "../../components/BackButton";
import PositiveButton from "../../components/PositiveButton";
import TitleDetailForm from "../../components/Title/TitleDetailForm";

export default function AdminTitlesDetailPage() {
  const navigate = useNavigate();
  const { titleId } = useParams();
  const { getTitle, refreshTitleFromIgdb } = useTitlesService();


  const [isLoading, setIsLoading] = useState(false);
  const [initiated, setInitiated] = useState(false);
  const [title, setTitle] = useState<Title | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (!initiated) {
      setIsLoading(true);
      setInitiated(true);
    }

    getTitle(titleId as string)
      .then((title) => {
        setTitle(title);
      })
      .catch((error) => {
        console.error("Error fetching title:", error);
      });
  }, [initiated]);

  useEffect(() => {
    if (title) {
      setIsLoading(false);
    }
  }, [title]);

  const navigateToTitleAnalytics = (selectedTitle: Title) => {
    navigate(`/analytics/titles?titleId=${selectedTitle.id}`);
  }

  const refreshFromIgdb = async () => {
    if (isRefreshing || !title) {
      return;
    }

    setIsRefreshing(true);
    setRefreshError(null);

    try {
      // The API saves and returns the refreshed title, so the form remounts on the new data
      // rather than showing values that no longer match what is stored.
      setTitle(await refreshTitleFromIgdb(title.id));
      setRefreshedAt(new Date());
    } catch (error) {
      console.error("Error refreshing title from IGDB:", error);
      setRefreshError(
        typeof error === "string"
          ? error
          : "Could not refresh this title from IGDB."
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <section className="flex flex-col gap-2 divide-stone-900">
      <div className="flex flex-row">
        <BackButton />
        <PageTitle className="text-4xl">
          {title ? title.name : `Loading`}{" "}
          {!title && <LoadingEllipsis />}
        </PageTitle>
      </div>
      <div className="grid grid-cols-1 gap-4">
        {title && <a className="card p-4" href={`/analytics/titles?titleId=${title.id}`} onClick={(e) => {
          e.preventDefault();
          navigateToTitleAnalytics(title);
        }}>
          <h2 className="text-2xl mb-2">Analytics Overview</h2>
          <div className="flex flex-row gap-2 items-center">
            <span>View analytics for this title</span>
            <ArrowForwardTwoToneIcon />
          </div>
        </a>}
        {title && (
          <div className="card p-4 flex flex-col gap-3">
            <div className="flex flex-row gap-4 items-center flex-wrap">
              <div className="flex flex-col gap-1">
                <h2 className="text-2xl">Refresh from IGDB</h2>
                <p className="text-sm opacity-80">
                  Re-reads the name, description, cover art, websites, genres,
                  themes and platforms from IGDB and saves them over the top.
                </p>
              </div>

              <PositiveButton
                className="ml-auto"
                onClick={refreshFromIgdb}
                disabled={isRefreshing || !title.igdbId}
                title={
                  title.igdbId
                    ? "Pull the latest details from IGDB"
                    : "This title has no IGDB ID to refresh from"
                }
              >
                {isRefreshing ? "Refreshing..." : "Refresh from IGDB"}
              </PositiveButton>
            </div>

            {/* The button is disabled in this case, but say why rather than leave it inert. */}
            {!title.igdbId && (
              <p className="text-sm text-amber-300">
                This title has no IGDB ID. Set one in the details below and save,
                then refresh.
              </p>
            )}

            {refreshError && (
              <div className="p-3 rounded bg-red-100 text-red-900 text-sm">
                {refreshError}
              </div>
            )}

            {refreshedAt && !refreshError && (
              <div className="p-3 rounded bg-blue-100 text-blue-900 text-sm">
                Refreshed at {refreshedAt.toLocaleTimeString()}.
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-2 col-span-2">
          <h2 className="text-2xl">Details</h2>
          {title ? (
            // Keyed on lastUpdated so a refresh rebuilds the form from the saved values instead of
            // leaving the old ones in its local state.
            <TitleDetailForm
              key={`${title.id}-${title.lastUpdated ?? ""}`}
              title={title}
            />
          ) : (
            <Ghost additionalClasses="h-24 col-span-2 rounded" />
          )}
        </div>
      </div>
    </section>
  );
}
